import { useQuery } from '@tanstack/react-query';

import { base, capabilitiesOrBaseline, readCapabilities } from '@/app/cancellations/engine/api-shapes';
import { type ICancellationCapabilities } from '@/app/cancellations/types';
import { httpStatusOf } from '@/app/live-election/engine/capability';
import { coreApi } from '@/lib/api-client';

import { cancellationKeys } from './keys';

export const fetchCancellationCapabilities = async (
	electionId: number,
	signal?: AbortSignal
): Promise<ICancellationCapabilities> => {
	try {
		const { data } = await coreApi.get(`${base(electionId)}/capabilities`, { signal });
		return readCapabilities(data);
	} catch (error) {
		return capabilitiesOrBaseline(error);
	}
};

/**
 * What this api.core can do. A 404 resolves to the v1 baseline instead of erroring, so every
 * caller can branch on flags without a separate "absent" path. Shared by the shell alert host
 * and the page — one request per election per 5 minutes.
 */
export function useCancellationCapabilities(electionId: number, enabled = true) {
	return useQuery({
		queryKey: cancellationKeys.capabilities(electionId),
		queryFn: ({ signal }) => fetchCancellationCapabilities(electionId, signal),
		enabled: enabled && electionId > 0,
		staleTime: 5 * 60_000,
		retry: (count, error) => {
			const s = httpStatusOf(error);
			return s !== 401 && s !== 403 && s !== 429 && count < 2;
		},
	});
}
